import React, { useEffect, useState } from 'react';
import { activityAPI } from '../utils/api';
import buildJourney from '../utils/buildJourney';
import SafeHtml from './SafeHtml';

// Colour mapping for each event type — hue matches the status where possible
// so the timeline reads consistently with the Reports status pills.
const STATUS_HUE = {
  Applied:     '#6366f1',
  Screening:   '#a78bfa',
  Shortlisted: '#f59e0b',
  Interview:   '#3b82f6',
  Hold:        '#eab308',
  Dropped:     '#94a3b8',
  Rejected:    '#ef4444',
  Hired:       '#10b981',
};

const eventStyle = (ev) => {
  if (ev.type === 'status_change') return { color: STATUS_HUE[ev.to] || '#64748b', icon: '→' };
  if (ev.type === 'submitted')     return { color: '#6366f1', icon: '＋' };
  if (ev.type === 'interview_scheduled') return { color: '#3b82f6', icon: '📅' };
  if (ev.type === 'interview_result') {
    if (ev.result === 'Pass') return { color: '#10b981', icon: '✓' };
    if (ev.result === 'Fail') return { color: '#ef4444', icon: '✗' };
    return { color: '#64748b', icon: '•' };
  }
  if (ev.type === 'reason') return { color: '#f59e0b', icon: '!' };
  if (ev.type === 'note')   return { color: '#8b5cf6', icon: '📝' };
  return { color: '#64748b', icon: '•' };
};

const fmtFull = (d) => new Date(d).toLocaleString('en-GB', {
  day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

// Compact relative-time — "just now", "3h ago", "2d ago", "Jun 15"
const fmtRelative = (iso) => {
  const now  = Date.now();
  const then = new Date(iso).getTime();
  const diff = Math.max(0, now - then);
  const mins = Math.round(diff / 60000);
  if (mins < 1)     return 'just now';
  if (mins < 60)    return `${mins}m ago`;
  const hrs  = Math.round(mins / 60);
  if (hrs < 24)     return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7)     return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

// Renders a compact vertical timeline for a single submission. Fetches
// activity logs by submission id and transforms them via buildJourney.
export default function SubmissionJourney({ submissionId, submittedAt }) {
  const [logs,    setLogs]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    if (!submissionId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    activityAPI.getByEntity(submissionId)
      .then(({ data }) => { if (!cancelled) setLogs(data.logs || []); })
      .catch(() => { if (!cancelled) setError('Could not load history.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [submissionId]);

  if (loading) {
    return <div className="text-xs italic py-3 px-2" style={{ color: 'var(--text-muted)' }}>Loading journey…</div>;
  }
  if (error) {
    return <div className="text-red-400 text-xs py-3 px-2">{error}</div>;
  }

  const events = buildJourney(logs || [], submittedAt);

  if (events.length === 0) {
    return <div className="text-xs italic py-3 px-2" style={{ color: 'var(--text-muted)' }}>No activity recorded yet for this submission.</div>;
  }

  return (
    <div className="py-2 px-1">
      <ol className="relative ml-3" style={{ borderLeft: '2px solid var(--border-subtle)' }}>
        {events.map((ev, i) => {
          const { color, icon } = eventStyle(ev);
          const isLast = i === events.length - 1;
          // Composite key survives re-sort / re-fetch better than array index.
          const key = `${ev.type}-${ev.at}-${ev.round ?? i}`;
          return (
            <li key={key} className="pl-5 pb-3 relative" style={{ paddingBottom: isLast ? 0 : 14 }}>
              <span
                className="absolute flex items-center justify-center rounded-full text-[10px] font-bold text-white"
                style={{
                  width: 20, height: 20,
                  left: -11, top: 0,
                  background: color,
                  // Halo matches the surrounding surface so it renders correctly on
                  // both dark and light themes (both define --bg-card).
                  boxShadow: `0 0 0 3px var(--bg-card)`,
                }}
                title={ev.type}
              >
                {icon}
              </span>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-sm font-medium break-words" style={{ color, overflowWrap: 'anywhere', wordBreak: 'break-word', minWidth: 0 }}>{ev.label}</span>
                <span className="text-[11px]" style={{ color: 'var(--text-muted)' }} title={fmtFull(ev.at)}>
                  {fmtRelative(ev.at)}
                </span>
              </div>
              {ev.detail && (
                <SafeHtml as="div" html={ev.detail} className="text-xs mt-0.5 leading-snug break-words" style={{ color: 'var(--text-secondary)', overflowWrap: 'anywhere', wordBreak: 'break-word', minWidth: 0 }} />
              )}
              <p className="text-[11px] mt-0.5 break-words" style={{ color: 'var(--text-muted)', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                by <span style={{ color: 'var(--text-secondary)' }}>{ev.actor?.name || '—'}</span>
                {ev.actor?.role ? <span> · {ev.actor.role}</span> : null}
                {ev.actor?.impersonatedBy
                  ? <span className="text-amber-400"> (acting as {ev.actor.impersonatedBy})</span>
                  : null}
              </p>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
